import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/daeoo6htto0ceqqnh1n0";

type Status = "idle" | "loading" | "success" | "error";

export default function Subscribe() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 防垃圾 honeypot 命中：不提交任何内容，仅给出与正常一致的成功反馈
    const honeypot = ((fd.get("company_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "company_alt") return; // 空的 honeypot 字段不随请求发送
      payload.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let code = "";
      let serverMsg = "";
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code || "";
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      } catch {
        serverMsg = responseText || "";
      }
      const isSpam = serverMsg.toLowerCase().includes("spam") || responseText.toLowerCase().includes("spam");
      if (res.ok && !isSpam && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  }

  return (
    <section id="subscribe" className="w-full bg-gradient-to-b from-[#f6efe3] to-[#faf6ee]">
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid overflow-hidden rounded-2xl border border-orange-900/10 lg:grid-cols-2">
          {/* 左侧文案 */}
          <div className="relative flex flex-col justify-center bg-red-700 p-6 text-white md:p-10">
            <div className="pointer-events-none absolute -right-6 -top-6 flex h-40 w-40 items-center justify-center rounded-full bg-white/10">
              <i className="ri-fire-fill text-6xl text-white/20"></i>
            </div>
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
              <i className="ri-flashlight-fill text-amber-300"></i>
              正在追踪 6 起热门事件
            </span>
            <h2 className="font-masthead mt-4 text-2xl font-black leading-tight md:text-3xl">
              订阅推送，紧跟每场反转
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/85">
              填写邮箱，每当事件进入关键反转或当事人作出最新回应，第一时间把更新进度送到你的收件箱，不再漏掉关键进展。
            </p>
            <ul className="mt-5 space-y-2 text-sm text-white/85">
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-amber-300"></i>
                关键进展更新即时提醒
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-amber-300"></i>
                每周「热瓜周报」精华整理
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-amber-300"></i>
                随时可退订，绝不打扰
              </li>
            </ul>
          </div>

          {/* 右侧表单 */}
          <div className="flex flex-col justify-center bg-white p-6 md:p-10">
            {status === "success" ? (
              <div className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="font-masthead mt-4 text-xl font-bold text-stone-900">订阅成功！</h3>
                <p className="mt-2 text-sm text-stone-500">
                  已为你开通推送提醒，下一次关键进展更新时会第一时间送达。记得留意收件箱确认邮件。
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-5 whitespace-nowrap rounded-md border border-orange-900/15 bg-[#faf6ee] px-4 py-2 text-sm font-medium text-stone-700 hover:text-red-700"
                >
                  再订阅其他邮箱
                </button>
              </div>
            ) : (
              <form
                data-readdy-form="gua-subscribe"
                onSubmit={onSubmit}
                className="flex flex-col gap-4"
              >
                <div>
                  <label htmlFor="sub-email" className="mb-1.5 block text-sm font-medium text-stone-700">
                    邮箱地址 <span className="text-red-700">*</span>
                  </label>
                  <div className="flex items-center gap-2 rounded-md border border-orange-900/15 bg-[#faf6ee] px-3 focus-within:border-red-700">
                    <i className="ri-mail-line text-stone-400"></i>
                    <input
                      id="sub-email"
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="请输入你的邮箱"
                      className="w-full bg-transparent py-2.5 text-sm text-stone-800 outline-none placeholder:text-stone-400"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="sub-note" className="mb-1.5 block text-sm font-medium text-stone-700">
                    最想优先追踪的方向 <span className="text-xs font-normal text-stone-400">（选填）</span>
                  </label>
                  <div className="flex items-start gap-2 rounded-md border border-orange-900/15 bg-[#faf6ee] px-3 focus-within:border-red-700">
                    <i className="ri-chat-smile-2-line mt-2.5 text-stone-400"></i>
                    <textarea
                      id="sub-note"
                      name="interests"
                      rows={3}
                      maxLength={500}
                      placeholder="如：带货翻车、明星解约、抄袭争议等方向"
                      className="w-full resize-none bg-transparent py-2.5 text-sm text-stone-800 outline-none placeholder:text-stone-400"
                    ></textarea>
                  </div>
                </div>

                {/* 反垃圾蜜罐字段 */}
                <div className="field-note-skip" aria-hidden="true">
                  <label htmlFor="company_alt">公司名称</label>
                  <input
                    type="text"
                    id="company_alt"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "error" && (
                  <p className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {formError || "提交失败，请稍后重试。"}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="flex items-center justify-center whitespace-nowrap rounded-md bg-red-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line mr-1.5 animate-spin"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-notification-3-line mr-1.5"></i>
                      立即订阅追踪进度
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-stone-400">
                  提交即代表同意接收本站推送邮件，我们承诺绝不发送垃圾邮件。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}